import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

/** Preferência do usuário: Claro (padrão), Escuro ou Automático (segue o sistema). */
export type ThemePref = "claro" | "escuro" | "automatico";
export const THEME_LABEL: Record<ThemePref, string> = { claro: "Claro", escuro: "Escuro", automatico: "Automático" };
const KEY = "mm.themePref";

const readLocal = (): ThemePref => {
  try {
    const v = localStorage.getItem(KEY);
    return v === "escuro" || v === "automatico" || v === "claro" ? v : "claro";
  } catch {
    return "claro";
  }
};
const systemDark = () => typeof window !== "undefined" && !!window.matchMedia?.("(prefers-color-scheme: dark)").matches;
const resolve = (p: ThemePref): "light" | "dark" => (p === "escuro" || (p === "automatico" && systemDark()) ? "dark" : "light");

interface Ctx { pref: ThemePref; resolved: "light" | "dark"; setPref: (p: ThemePref) => void }
const ThemeCtx = createContext<Ctx | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [pref, setPrefState] = useState<ThemePref>(readLocal);
  const [sys, setSys] = useState(systemDark);
  useEffect(() => {
    const mq = window.matchMedia?.("(prefers-color-scheme: dark)");
    if (!mq) return;
    const h = () => setSys(mq.matches);
    mq.addEventListener("change", h);
    return () => mq.removeEventListener("change", h);
  }, []);
  const resolved: "light" | "dark" = pref === "escuro" || (pref === "automatico" && sys) ? "dark" : "light";
  useEffect(() => { document.documentElement.setAttribute("data-theme", resolved); }, [resolved]);
  const setPref = useCallback((p: ThemePref) => {
    setPrefState(p);
    try { localStorage.setItem(KEY, p); } catch { /* sem armazenamento local: a preferência do usuário continua no servidor */ }
  }, []);
  const value = useMemo(() => ({ pref, resolved, setPref }), [pref, resolved, setPref]);
  return <ThemeCtx.Provider value={value}>{children}</ThemeCtx.Provider>;
}

export function useTheme() {
  const c = useContext(ThemeCtx);
  if (!c) throw new Error("useTheme fora do ThemeProvider");
  return c;
}
export { resolve as resolveTheme };
